import React, { useEffect, useRef, useCallback } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import VolumeUpIcon from '@material-ui/icons/VolumeUp'
import { FormattedMessage } from 'react-intl'
import useAudioPlayer from './hooks/useAudioPlayer'
import SceneBackground from './SceneBackground'
import renderFormattedText from './utils/renderFormattedText'
import { playerMessages } from './CapeeshPlayer.i18n.Messages'

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        gap: 20,
        padding: '12px 20px 20px',
        overflow: 'auto',
        '@media (min-width: 600px) and (orientation: landscape)': {
            gap: 12,
            padding: '8px 16px 16px',
        },
        '@media (max-width: 599px)': {
            padding: '0 0 12px',
            gap: 12,
            overflow: 'hidden',
        },
    },
    sceneContainer: {
        width: '100%',
        maxWidth: 520,
        flexShrink: 0,
        minHeight: 100,
        borderRadius: 16,
        '@media (min-width: 600px) and (orientation: landscape)': {
            maxWidth: 'min(520px, 45vh)',
        },
        '@media (max-width: 599px)': {
            maxWidth: 'none',
            borderRadius: 0,
            flex: 1,
        },
    },
    audioIndicator: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 80,
        height: 80,
        borderRadius: '50%',
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        backdropFilter: 'blur(8px)',
        flexShrink: 0,
        '@media (max-width: 599px)': {
            width: 56,
            height: 56,
        },
    },
    audioIcon: {
        fontSize: 40,
        color: '#FFFFFF',
    },
    audioIconPlaying: {
        animation: '$pulse 1.5s ease-in-out infinite',
    },
    '@keyframes pulse': {
        '0%, 100%': { transform: 'scale(1)', opacity: 1 },
        '50%': { transform: 'scale(1.15)', opacity: 0.8 },
    },
    subtitleContainer: {
        maxWidth: 480,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
        '@media (max-width: 599px)': {
            maxWidth: '100%',
            padding: '0 12px',
        },
    },
    subtitleText: {
        fontFamily: 'Rubik',
        fontSize: 18,
        fontWeight: 400,
        lineHeight: '170%',
        color: '#FFFFFF',
        textAlign: 'center',
    },
    titleText: {
        fontFamily: 'Rubik',
        fontSize: 22,
        fontWeight: 600,
        color: '#FFFFFF',
        textAlign: 'center',
    },
    skipButton: {
        padding: '10px 32px',
        borderRadius: 24,
        border: '2px solid rgba(255, 255, 255, 0.4)',
        background: 'transparent',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
        opacity: 0.7,
        transition: 'opacity 200ms ease',
        flexShrink: 0,
        '&:hover': {
            opacity: 1,
            borderColor: 'rgba(255, 255, 255, 0.7)',
        },
    },
}))

const ListenNode = ({ node, previousNode, imageMap, audioMap, onNodeComplete, isNativeSpeaker = false, l2LanguageId }) => {
    const classes = useStyles()
    const voicePlayer = useAudioPlayer()
    const bgPlayer = useAudioPlayer()
    const hasAdvanced = useRef(false)

    const animationDuration = node.scenarioMentors?.[0]?.animationLengthInSeconds || 1.5

    const finish = useCallback(() => {
        if (hasAdvanced.current) return
        hasAdvanced.current = true
        voicePlayer.stopAudio()
        bgPlayer.stopAudio()
        onNodeComplete({
            nodeId: node.nodeId,
            chosenAlternativeIndex: 0,
        })
    }, [node.nodeId, onNodeComplete, voicePlayer, bgPlayer])

    useEffect(() => {
        hasAdvanced.current = false

        const bgAudioUrl = node.backgroundAudioUrl ? audioMap[node.backgroundAudioUrl] : null
        if (bgAudioUrl) {
            bgPlayer.playAudio(bgAudioUrl, { loop: true, volume: 0.15 })
        }

        const voiceAudioUrl = node.options?.[0]?.textAudioUrl
        const blobUrl = voiceAudioUrl ? audioMap[voiceAudioUrl] : null

        if (blobUrl) {
            voicePlayer.playAudio(blobUrl).then(() => {
                setTimeout(finish, 500)
            })
        } else {
            setTimeout(finish, 2000)
        }

        return () => {
            voicePlayer.stopAudio()
            bgPlayer.stopAudio()
        }
    }, [])

    const allTexts = (node.texts || []).filter(
        (t) => !isNativeSpeaker || !l2LanguageId || t.textLanguageId === l2LanguageId || !t.textLanguageId
    )
    const subtitleTexts = allTexts.filter((t) => t.textType === 4)
    const titleTexts = allTexts.filter((t) => t.textType === 1 || t.textType === 2)
    const otherTexts = allTexts.filter(
        (t) => t.textType !== 4 && t.textType !== 1 && t.textType !== 2
    )

    return (
        <div className={classes.container}>
            <div className={classes.sceneContainer}>
                <SceneBackground
                    node={node}
                    previousNode={previousNode}
                    imageMap={imageMap}
                    duration={animationDuration}
                />
            </div>

            <div className={classes.audioIndicator}>
                <VolumeUpIcon
                    className={`${classes.audioIcon} ${voicePlayer.isPlaying ? classes.audioIconPlaying : ''}`}
                />
            </div>

            {titleTexts.map((txt, idx) => (
                <Typography key={`t-${idx}`} className={classes.titleText}>
                    {renderFormattedText(txt.text)}
                </Typography>
            ))}

            <div className={classes.subtitleContainer}>
                {subtitleTexts.map((txt, idx) => (
                    <Typography key={`s-${idx}`} className={classes.subtitleText}>
                        {renderFormattedText(txt.text)}
                    </Typography>
                ))}
                {otherTexts.map((txt, idx) => (
                    <Typography key={`o-${idx}`} className={classes.subtitleText}>
                        {renderFormattedText(txt.text)}
                    </Typography>
                ))}
            </div>

            <button className={classes.skipButton} onClick={finish}>
                <FormattedMessage {...playerMessages.skip} />
            </button>
        </div>
    )
}

export default ListenNode
